import { Canvas } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { DEFAULT_SITE_CONDITIONS } from "../../domain/foundation/siteConditions";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import { normalizeIndoorRoom } from "../../domain/indoor/indoorRoom";
import type { InteriorLayout } from "../../domain/interior";
import {
	DEFAULT_SAUNA_ROTATION_DEG,
	rotateSlopeToWorldFrame,
	type SaunaRotationDeg,
} from "../../domain/viewer/saunaRotation";
import { normalizeTimeOfDay } from "../../domain/viewer/timeOfDay";
import { deriveInteriorLayoutContext } from "../../engines/interiorLayoutResolver";
import {
	configureCompressedTextureSupport,
	disposeMaterialLibrary,
	prewarmCompressedTextureTranscoder,
} from "../../engines/materials";
import type {
	BuildInput,
	CameraPreset,
	SaunaEngine,
} from "../../engines/types";
import { BakedContactShadows } from "./BakedContactShadows";
import { BlueprintOverride } from "./BlueprintOverride";
import {
	type AssemblyFilmFrame,
	AssemblyFilmOverride,
	type AssemblyFilmSceneInfo,
} from "./assemblyFilm/AssemblyFilmOverride";
import type { BuildStepView } from "./buildStepView";
import { DemandPerformanceMonitor } from "./DemandPerformanceMonitor";
import { DragController } from "./DragController";
import { HeaterClearanceOverlay } from "./HeaterClearanceOverlay";
import type { InteriorDragPreview } from "./InteriorDragController";
import { InteriorEditHandles } from "./InteriorEditHandles";
import { deriveInteriorEditSetup } from "./interiorHandles";
import { LightShafts } from "./LightShafts";
import { GRAVEL_LIFT_M } from "./landscapeGeometry";
import { MeasurementSceneBadges } from "./MeasurementSceneBadges";
import { MeasurementSceneGuides } from "./MeasurementSceneGuides";
import { PartitionDragAffordance } from "./PartitionDragAffordance";
import { RenderController } from "./RenderController";
import { RotationDragAffordance } from "./RotationDragAffordance";
import { SaunaModel } from "./SaunaModel";
import type { SceneVisibility } from './sceneVisibility';
import { SaunaRig } from "./SaunaRig";
import { ScaleFigures } from "./ScaleFigures";
import { SceneAccentLighting } from "./SceneAccentLighting";
import { SceneChimneySmoke } from "./SceneChimneySmoke";
import type { OrbitControlsRef } from "./SceneControls";
import { SceneControls } from "./SceneControls";
import { SceneEnvironment } from "./SceneEnvironment";
import {
	getGroundBaseY,
	getLowestShadowReceiverY,
} from "./SceneEnvironment.helpers";
import { SceneIndoorWalls } from "./SceneIndoorRoom";
import { SceneLighting } from "./SceneLighting";
import { ScenePostProcessing } from "./ScenePostProcessing";
import { SectionView } from "./SectionView";
import type { ScaleFigureCapacity } from "./scaleFigureLayout";
import {
	resolveWorldSectionAxis,
	type SectionAxis,
	type SectionLayerFamily,
} from "./sectionLayers";
import {
	DEFAULT_MAX_SHADOW_LENGTH_M,
	measureShadowCasterBounds,
} from "./sunShadowFrustum";
import { ViewerFrameReadySignal } from "./ViewerFrameReadySignal";
import {
	type MeasurementHoverState,
	type MeasurementTarget,
	ViewerMeasurementController,
} from "./ViewerMeasurementController";
import { ViewerRendererDiagnostics } from "./ViewerRendererDiagnostics";
import { CAMERA_FAR_M, CAMERA_NEAR_M } from "./viewerCameraLimits";
import type {
	TimeOfDay,
	ViewerPerformanceSample,
	ViewerPresentationProfile,
	ViewerRuntimeProfile,
	ViewMode,
} from "./viewerConfig";
import type {
	MeasurementSceneBadge,
	MeasurementSceneGuide,
	PartitionMeasurementPreview,
} from "./viewerMeasurements";
import {
	captureViewerSnapshot,
	type ViewerSnapshotOptions,
} from "./viewerSnapshot";

interface SceneCanvasProps {
	buildInput: BuildInput;
	visibility: SceneVisibility;
	engine: SaunaEngine;
	runtimeProfile: ViewerRuntimeProfile;
	presentationProfile: ViewerPresentationProfile;
	activePreset: CameraPreset;
	timeOfDay: TimeOfDay;
	isTimeOfDayScrubbing?: boolean;
	viewMode: ViewMode;
	/**
	 * Whole-building orientation on the plot. Applied as ONE `rotation.y` on
	 * the sauna rig — the site (ground, lake, sun, dressing) never turns.
	 */
	rotationDeg?: SaunaRotationDeg;
	isDraggingWindow: { widthMm: number; heightMm: number } | null;
	showOpeningGuides: boolean;
	/** Viewer-only scale figures seated on the benches. Default off. */
	showScaleFigures?: boolean;
	onScaleFigureCapacityChange?: (capacity: ScaleFigureCapacity | null) => void;
	/** Upper bound on the scale-figure count, from the wizard's size tier. */
	scaleFigureCapacityHint?: number | null;
	interactive?: boolean;
	stepView?: BuildStepView | null;
	foundationProgress?: number;
	assemblyReducedMotion?: boolean;
	/** One frame of the assembly film; null/absent leaves the scene as built. */
	assemblyFilm?: AssemblyFilmFrame | null;
	onAssemblyFilmSceneInfo?: (info: AssemblyFilmSceneInfo) => void;
	onAssemblyFilmScaleChange?: (pxPerMeter: number) => void;
	sectionActive?: boolean;
	sectionAxis?: SectionAxis;
	sectionPosition?: number;
	sectionFlip?: boolean;
	onSectionPositionChange?: (position: number) => void;
	onSectionFamiliesChange?: (families: SectionLayerFamily[]) => void;
	onWindowPlaced?: (opening: WallOpening) => void;
	onOpeningMoved?: (opening: WallOpening) => void;
	onPartitionSplitChange?: (partitionSplit: number) => void;
	/** Commit a new whole-building orientation (already snapped to a cardinal). */
	onRotationChange?: (rotationDeg: number) => void;
	onDragCancelled?: () => void;
	measurementBadges: MeasurementSceneBadge[];
	measurementGuides: MeasurementSceneGuide[];
	onMeasurementHoverChange?: (state: MeasurementHoverState | null) => void;
	onMeasurementSelectionChange?: (target: MeasurementTarget | null) => void;
	onPartitionDragPreviewChange?: (
		preview: PartitionMeasurementPreview | null,
	) => void;
	/**
	 * Mount the interior drag handles (lavar + aggregat, issue 133).
	 *
	 * A MODE rather than an always-on affordance for one concrete reason: the
	 * picker resolves by distance, so the full-coverage wall planes have to stand
	 * down for anything inside the room to be reachable — and standing them down
	 * unconditionally would break wall measurement. See
	 * `ViewerMeasurementController.getMeasurementObjects`.
	 */
	interiorEditing?: boolean;
	onInteriorLayoutChange?: (layout: InteriorLayout) => void;
	onInteriorDragPreviewChange?: (preview: InteriorDragPreview | null) => void;
	/**
	 * Whether this design HAS a layout-driven interior. Only the deprecated barrel
	 * does not, and answering it means resolving the layout — so the answer comes
	 * from here, where that work already happens, rather than from a second copy of
	 * the resolver's barrel test. Without it the toggle is a live control that
	 * strips the front wall and mounts nothing.
	 */
	onInteriorEditAvailableChange?: (available: boolean) => void;
	onSnapshotReady?: (
		getSnapshot: (options?: ViewerSnapshotOptions) => string | null,
	) => void;
	/** Fires only after textures settle and consecutive frames actually paint. */
	onFrameReady?: () => void;
	onRendererReady?: () => void;
	onRendererContextLost?: () => void;
	onRendererContextRestored?: () => void;
	onPerformanceChange?: (sample: ViewerPerformanceSample) => void;
	performanceMonitorResetKey?: string;
}

/**
 * Orchestrator: R3F Canvas with all scene components.
 * Replaces the imperative Three.js init that was in Viewer3D.tsx.
 */
export function SceneCanvas({
	buildInput,
	visibility,
	engine,
	runtimeProfile,
	presentationProfile,
	activePreset,
	timeOfDay,
	isTimeOfDayScrubbing = false,
	viewMode,
	rotationDeg = DEFAULT_SAUNA_ROTATION_DEG,
	isDraggingWindow,
	showOpeningGuides,
	showScaleFigures = false,
	onScaleFigureCapacityChange,
	scaleFigureCapacityHint,
	interactive = true,
	stepView,
	foundationProgress,
	assemblyReducedMotion,
	assemblyFilm = null,
	onAssemblyFilmSceneInfo,
	onAssemblyFilmScaleChange,
	sectionActive = false,
	sectionAxis = "x",
	sectionPosition = 0.5,
	sectionFlip = false,
	onSectionPositionChange,
	onSectionFamiliesChange,
	onWindowPlaced,
	onOpeningMoved,
	onPartitionSplitChange,
	onRotationChange,
	onDragCancelled,
	measurementBadges,
	measurementGuides,
	onMeasurementHoverChange,
	onMeasurementSelectionChange,
	onPartitionDragPreviewChange,
	interiorEditing = false,
	onInteriorLayoutChange,
	onInteriorDragPreviewChange,
	onInteriorEditAvailableChange,
	onSnapshotReady,
	onFrameReady,
	onRendererReady,
	onRendererContextLost,
	onRendererContextRestored,
	onPerformanceChange,
	performanceMonitorResetKey,
}: SceneCanvasProps) {
	const controlsRef = useRef<OrbitControlsRef>(null);
	// The single node every model-relative object hangs off. Rotating the
	// building is one write to `saunaRig.rotation.y`; anything that must stay
	// world-fixed (ground, lake, sun, contact shadows) is deliberately outside.
	const saunaRigRef = useRef<THREE.Group>(null);
	const rendererLifecycleCleanupRef = useRef<(() => void) | null>(null);
	// Read by the snapshot getter, which is registered once on renderer
	// creation and must not re-register when a layer toggles.
	const ventilationDiagramRef = useRef(visibility.ventilation);
	const resolvedTimeOfDay = normalizeTimeOfDay(timeOfDay);
	const interiorView = activePreset.id === "interior-peek" || interiorEditing;
	const [modelRevision, setModelRevision] = useState(0);
	// Bumped once per settled rotation. SectionView needs its own signal
	// because a rotation clips no new meshes, so nothing else tells it that
	// the world matrices its stencil shells froze are now stale.
	const [rigRevision, setRigRevision] = useState(0);
	// Shadow-caster bounds, re-measured on every dirty-tracked rebuild
	// and handed to the key light's shadow-camera fit (issue 121). The
	// identity is preserved when the box is unchanged, so a notification
	// that moved no geometry — a visibility toggle, a step-view change —
	// doesn't re-fit the shadow camera.
	const [modelBounds, setModelBounds] = useState<THREE.Box3 | null>(null);
	// The shadow lands on the ground plane SceneEnvironment draws, which
	// sits below y = 0 by the joist height + visible plinth (and keeps
	// falling on a sloped site). The fit needs that height or its far
	// plane stops short of the real receiver.
	const shadowReceiverY = useMemo(
		() =>
			getLowestShadowReceiverY(
				buildInput.siteConditions ?? DEFAULT_SITE_CONDITIONS,
				DEFAULT_MAX_SHADOW_LENGTH_M,
			),
		[buildInput.siteConditions],
	);
	// Flat ground under the building, for the dusk sockel light pools. A
	// sloping plot gets none: a flat pool would float over the downhill side
	// and cut into the uphill one. The rig only turns about Y, so this
	// world height is also the rig-frame height.
	// Indoors (issue 175) the room has no sockel and no plot: no light pools.
	const isIndoor = buildInput.siting === "indoor";
	const accentGroundY = useMemo(() => {
		if (isIndoor) return null;
		const site = buildInput.siteConditions ?? DEFAULT_SITE_CONDITIONS;
		return site.terrainMode === "slope" ? null : getGroundBaseY(site);
	}, [buildInput.siteConditions, isIndoor]);
	// A flat bake belongs on the gravel, below the plinths. On a slope the
	// sun's terrain-received shadow suffices; a horizontal decal would float.
	const contactShadowGroundY = isIndoor
		? 0
		: accentGroundY === null ? null : accentGroundY + GRAVEL_LIFT_M;
	const indoorRoom = useMemo(
		() => normalizeIndoorRoom(buildInput.indoorRoom),
		[buildInput.indoorRoom],
	);
	// The room's own ceiling sits a little above the sauna's: floor joists
	// and subfloor (≈0.17 m), the chosen wall height, then clear space.
	const indoorRoomHeightM = resolveWallHeightM(buildInput.wallHeightM) + 0.6;
	// `BuildInput.siteConditions` carries the slope in the BUILDING's frame so
	// the piers get the ground height that is really under them (see
	// `rotateSlopeToModelFrame`). The terrain mesh, the lake and the dressing
	// live outside the rig and never turn, so they need it turned back.
	// (`getLowestShadowReceiverY` above only reads the gradient's magnitude,
	// which the rotation preserves, so it is frame-agnostic.)
	const siteConditions = useMemo(
		() =>
			rotateSlopeToWorldFrame(
				buildInput.siteConditions ?? DEFAULT_SITE_CONDITIONS,
				rotationDeg,
			),
		[buildInput.siteConditions, rotationDeg],
	);
	// The cut direction is authored in MODEL space and applied in WORLD space.
	// A quarter turn swaps the axis; every turn but 0 also reverses at least
	// one axis, so the slider position and the kept half have to be mirrored
	// with it — and mirrored back on the way out of the scene handle.
	const worldSection = resolveWorldSectionAxis(sectionAxis, rotationDeg);
	const worldSectionPosition = worldSection.inverted
		? 1 - sectionPosition
		: sectionPosition;
	const worldSectionFlip = worldSection.inverted ? !sectionFlip : sectionFlip;
	const handleWorldSectionPositionChange = useCallback(
		(position: number) => {
			onSectionPositionChange?.(
				worldSection.inverted ? 1 - position : position,
			);
		},
		[onSectionPositionChange, worldSection.inverted],
	);
	/**
	 * The interior editing setup, derived ONCE per build input and shared by the
	 * handles and the drag controller. Nothing is derived while the mode is off, so
	 * a viewer nobody is editing pays nothing for the feature.
	 *
	 * Section view owns the pointer when it is active (its cut handle freezes world
	 * matrices), so interior editing stands down there exactly as the partition and
	 * measurement handles do.
	 */
	// `interiorEditing` already carries the mode's own stand-downs (section view, a
	// window placement) — ONE predicate, owned next to the toggle in `Viewer3D`, so
	// the front-wall override and the handles cannot disagree about whether the mode
	// is live. All that is left here is "can this canvas host handles at all".
	const interiorEdit = useMemo(
		() =>
			interiorEditing && interactive
				? deriveInteriorEditSetup(buildInput)
				: null,
		[buildInput, interactive, interiorEditing],
	);
	const interiorRoomSpanM = useMemo(() => {
		if (!interiorView) return undefined;
		const room =
			interiorEdit?.model.context.room ??
			deriveInteriorLayoutContext(buildInput)?.room;
		if (room) {
			return Math.min(room.maxXMm - room.minXMm, room.maxZMm - room.minZMm) / 1000;
		}
		// ponytail: deprecated barrel has no layout room; use its envelope until it does.
		return Math.min(buildInput.dimensions.width, buildInput.dimensions.length);
	}, [buildInput, interiorEdit, interiorView]);

	useEffect(() => {
		if (!interiorEditing || !interactive) return;
		onInteriorEditAvailableChange?.(interiorEdit !== null);
	}, [
		interactive,
		interiorEdit,
		interiorEditing,
		onInteriorEditAvailableChange,
	]);

	/**
	 * The live interior drag, held HERE rather than only in the parent: the heater
	 * clearance envelope has to follow the ghost, and that means re-resolving it
	 * against the candidate layout inside the canvas.
	 */
	const [interiorDragPreview, setInteriorDragPreview] =
		useState<InteriorDragPreview | null>(null);

	const handleInteriorDragPreviewChange = useCallback(
		(preview: InteriorDragPreview | null) => {
			// Stored ONLY when the clearance envelope will actually use it — i.e. a
			// heater drag, or the overlay already being on. A bench drag with the
			// overlay off changes nothing this canvas draws, and storing it anyway
			// reconciled the whole R3F subtree once per 10 mm step for an identical
			// scene. The parent still gets every preview, for the readout card.
			const usable =
				preview &&
				(preview.target.kind === "heater" || visibility.heaterClearance)
					? preview
					: null;
			setInteriorDragPreview(usable);
			onInteriorDragPreviewChange?.(preview);
		},
		[onInteriorDragPreviewChange, visibility.heaterClearance],
	);

	/**
	 * What the clearance envelope is measured against.
	 *
	 * During a heater drag it is the CANDIDATE layout, so the zone moves with the
	 * ghost and goes red on the placement the release would refuse. Drawing the
	 * committed envelope while the unit visibly sits somewhere else is the exact
	 * failure issue 131 documents — a green "safe" box around empty floor.
	 *
	 * Memoised on the preview so the (fairly heavy) re-resolve happens once per
	 * changed candidate rather than once per pointermove; the controller already
	 * suppresses previews that resolve to the same layout.
	 */
	const clearanceBuildInput = useMemo(
		() =>
			interiorDragPreview
				? { ...buildInput, interiorLayout: interiorDragPreview.layout }
				: buildInput,
		[buildInput, interiorDragPreview],
	);

	const handleSceneChanged = useCallback((bounds: THREE.Box3 | null) => {
		setModelRevision((revision) => revision + 1);
		setModelBounds((previous) =>
			previous && bounds && previous.equals(bounds) ? previous : bounds,
		);
	}, []);

	useEffect(() => {
		ventilationDiagramRef.current = visibility.ventilation;
	}, [visibility.ventilation]);

	// A rotation moves geometry without REBUILDING it, so SaunaModel's
	// dirty-tracking never fires and `onSceneChanged` never runs. Both shadow
	// consumers are derived from world-space bounds and would silently keep the
	// old orientation until some unrelated edit triggered a rebuild: the
	// contact shadow is a one-shot bake keyed on `modelRevision` (see
	// docs/notes/2026-07-13-hidden-render-multipliers-contact-shadows-and-msaa.md
	// — remounting it IS the re-bake), and the sun's shadow camera is fitted to
	// `modelBounds`, so a rectangular sauna turned 90° would cast a clipped
	// shadow from a detached blob.
	//
	// `SaunaRig` owns the timing, not this component: it lives inside the R3F
	// root, so it is the only place that can tell when the transform has
	// actually been written to the group. Measuring from an effect out here
	// reads the OLD angle. See the note in SaunaRig.
	const handleRotationSettled = useCallback(
		(rig: THREE.Group) => {
			handleSceneChanged(measureShadowCasterBounds(rig));
			setRigRevision((revision) => revision + 1);
		},
		[handleSceneChanged],
	);

	// Expose snapshot getter to parent via onCreated
	const handleCreated = useCallback(
		(state: {
			gl: THREE.WebGLRenderer;
			scene: THREE.Scene;
			camera: THREE.Camera;
		}) => {
			rendererLifecycleCleanupRef.current?.();

			const canvas = state.gl.domElement;
			const handleContextLost = (event: Event) => {
				event.preventDefault();
				onRendererContextLost?.();
			};
			const handleContextRestored = () => {
				onRendererContextRestored?.();
			};

			canvas.addEventListener(
				"webglcontextlost",
				handleContextLost as EventListener,
			);
			canvas.addEventListener(
				"webglcontextrestored",
				handleContextRestored as EventListener,
			);
			void configureCompressedTextureSupport(state.gl, () => {
				state.gl.render(state.scene, state.camera);
			});
			rendererLifecycleCleanupRef.current = () => {
				canvas.removeEventListener(
					"webglcontextlost",
					handleContextLost as EventListener,
				);
				canvas.removeEventListener(
					"webglcontextrestored",
					handleContextRestored as EventListener,
				);
			};

			if (onSnapshotReady) {
				onSnapshotReady((options) =>
					captureViewerSnapshot(state, ventilationDiagramRef.current, options),
				);
			}
			onRendererReady?.();
		},
		[
			onRendererContextLost,
			onRendererContextRestored,
			onRendererReady,
			onSnapshotReady,
		],
	);

	// Kick off the KTX2 transcoder import the instant this mounts — before the
	// first buildSauna3D and its shader compiles saturate the main thread.
	// `configureCompressedTextureSupport` (Canvas onCreated) blocks the ktx2
	// texture bindings until this import resolves; starved behind a heavy scene
	// build under SwiftShader that import took ~30 s, so warming it here in the
	// quiet pre-build window starts the transcodes ~26 s sooner. See
	// prewarmCompressedTextureTranscoder in engines/materials.ts.
	useEffect(() => {
		void prewarmCompressedTextureTranscoder();
	}, []);

	useEffect(
		() => () => {
			rendererLifecycleCleanupRef.current?.();
			rendererLifecycleCleanupRef.current = null;
			disposeMaterialLibrary();
		},
		[],
	);

	return (
		<Canvas
			data-test="viewer-ready"
			frameloop="demand"
			shadows={{ type: THREE.PCFShadowMap }}
			dpr={[1, runtimeProfile.pixelRatioCap]}
			camera={{ fov: 42, near: CAMERA_NEAR_M, far: CAMERA_FAR_M }}
			gl={{ preserveDrawingBuffer: false, antialias: true }}
			onCreated={handleCreated}
		>
			<DemandPerformanceMonitor
				onChange={onPerformanceChange}
				resetKey={performanceMonitorResetKey}
			/>
			<RenderController runtimeProfile={runtimeProfile} />
			<ViewerRendererDiagnostics key={runtimeProfile.tier} />
			<ViewerFrameReadySignal onReady={onFrameReady} />
			{runtimeProfile.contactShadowsEnabled && contactShadowGroundY !== null ? (
				<BakedContactShadows
					modelRevision={modelRevision}
					groundY={contactShadowGroundY}
					width={buildInput.dimensions.width}
					length={buildInput.dimensions.length}
					resolution={runtimeProfile.contactShadowResolution}
					blur={runtimeProfile.contactShadowBlur}
					opacity={runtimeProfile.contactShadowOpacity}
					color={resolvedTimeOfDay >= 0.75 ? "#0c0a08" : "#1a1408"}
				/>
			) : null}
			<SceneLighting
				interiorLighting={buildInput.thermalInputs.saunaLighting !== "none"}
				runtimeProfile={runtimeProfile}
				presentationProfile={presentationProfile}
				timeOfDay={resolvedTimeOfDay}
				isTimeOfDayScrubbing={isTimeOfDayScrubbing}
				modelBounds={modelBounds}
				modelRevision={modelRevision}
				shadowReceiverY={shadowReceiverY}
			/>
			<SceneEnvironment
				runtimeProfile={runtimeProfile}
				presentationProfile={presentationProfile}
				dimensions={buildInput.dimensions}
				deckConfig={buildInput.deckConfig}
				siteConditions={siteConditions}
				timeOfDay={resolvedTimeOfDay}
				rotationDeg={rotationDeg}
				indoor={isIndoor}
				interiorView={interiorView}
			/>
			<SceneControls activePreset={activePreset} controlsRef={controlsRef} />
			<ScenePostProcessing
				runtimeProfile={runtimeProfile}
				interiorRoomSpanM={interiorRoomSpanM}
				needsStencil={sectionActive && runtimeProfile.sectionCapsEnabled}
			/>
			{/* ── The sauna rig ────────────────────────────────
				    Everything MODEL-relative hangs off this one node, so
				    "rotate the building" is a single `rotation.y` instead of
				    a rotation each consumer has to re-derive: the built
				    sauna group (attached imperatively via `parentRef`), the
				    god rays that spill out of its windows, the measurement
				    guides/badges, the heater clearance envelope and the drag
				    pills. The site — ground, lake, sun, contact shadows —
				    stays outside on purpose: the whole point of the feature
				    is turning the building RELATIVE to the plot. */}
			<SaunaRig
				rotationDeg={rotationDeg}
				rigRef={saunaRigRef}
				onRotationSettled={handleRotationSettled}
			>
				<SaunaModel
					buildInput={buildInput}
					visibility={visibility}
					engine={engine}
					runtimeProfile={runtimeProfile}
					timeOfDay={resolvedTimeOfDay}
					showOpeningGuides={showOpeningGuides}
					stepView={stepView}
					foundationProgress={foundationProgress}
					assemblyReducedMotion={assemblyReducedMotion}
					parentRef={saunaRigRef}
					onSceneChanged={handleSceneChanged}
				/>
				{!interiorView && (
					<LightShafts
						buildInput={buildInput}
						runtimeProfile={runtimeProfile}
						timeOfDay={resolvedTimeOfDay}
					/>
				)}
				<SceneAccentLighting
					interiorLighting={buildInput.thermalInputs.saunaLighting !== "none"}
					modelRevision={modelRevision}
					timeOfDay={resolvedTimeOfDay}
					groundY={accentGroundY}
				/>
				{isIndoor ? (
					<SceneIndoorWalls
						dimensions={buildInput.dimensions}
						indoorRoom={indoorRoom}
						roomHeightM={indoorRoomHeightM}
					/>
				) : null}
				<SceneChimneySmoke
					// Indoors the flue leaves through the house roof, far above
					// the room this scene shows.
					enabled={
						runtimeProfile.chimneySmokeEnabled && !sectionActive && !isIndoor
					}
					modelRevision={modelRevision}
					timeOfDay={resolvedTimeOfDay}
				/>
				<MeasurementSceneBadges badges={measurementBadges} />
				<MeasurementSceneGuides guides={measurementGuides} />
				<HeaterClearanceOverlay
					buildInput={clearanceBuildInput}
					// Forced on for the duration of a heater drag: the "invalid zone
					// goes red in 3D" requirement IS this envelope, and it is the one
					// owner of the clearance geometry (issue 131).
					visible={
						visibility.heaterClearance ||
						interiorDragPreview?.target.kind === "heater"
					}
				/>
				<InteriorEditHandles setup={interiorEdit} />
				<ScaleFigures
					visible={showScaleFigures}
					modelRevision={modelRevision}
					onCapacityChange={onScaleFigureCapacityChange}
					maxFigures={scaleFigureCapacityHint}
				/>
				{interactive && (
					<>
						{/* Section mode owns the cut handle; pause partition +
							    measurement so their handles don't fight it. */}
						{/* Stood down during interior editing. The pick-layer filter only
							    governs CANVAS raycasting; these are drei `<Html>` pills with
							    `pointerEvents: auto`, so a door or window pill projected over a
							    bench swallows the pointer before the canvas ever sees it and
							    starts an opening drag instead. They are shell affordances, so
							    they follow the same layer rule as the shell's raycast targets. */}
						<PartitionDragAffordance
							buildInput={buildInput}
							visibility={visibility}
							active={
								!isDraggingWindow && !sectionActive && interiorEdit === null && visibility.dividerWall !== false
							}
							controlsRef={controlsRef}
							rotationDeg={rotationDeg}
							rigRef={saunaRigRef}
							onPartitionSplitChange={onPartitionSplitChange}
							onPartitionDragPreviewChange={onPartitionDragPreviewChange}
							onOpeningMoved={onOpeningMoved}
						/>
						<RotationDragAffordance
							dimensions={buildInput.dimensions}
							market={buildInput.market}
							rotationDeg={rotationDeg}
							rigRef={saunaRigRef}
							active={!isDraggingWindow && !sectionActive}
							controlsRef={controlsRef}
							onRotationChange={onRotationChange}
						/>
					</>
				)}
			</SaunaRig>
			<SectionView
				active={sectionActive}
				axis={worldSection.axis}
				position={worldSectionPosition}
				flip={worldSectionFlip}
				capsEnabled={runtimeProfile.sectionCapsEnabled}
				interactive={interactive}
				ventilationDiagram={visibility.ventilation}
				market={buildInput.market}
				rigRevision={rigRevision}
				controlsRef={controlsRef}
				onPositionChange={handleWorldSectionPositionChange}
				onFamiliesChange={onSectionFamiliesChange}
			/>
			{interactive && (
				<ViewerMeasurementController
					buildInput={buildInput}
					controlsRef={controlsRef}
					disabled={!!isDraggingWindow || sectionActive}
					rigRef={saunaRigRef}
					onHoverChange={onMeasurementHoverChange}
					onSelectionChange={onMeasurementSelectionChange}
					onOpeningMoved={onOpeningMoved}
					onPartitionSplitChange={onPartitionSplitChange}
					onPartitionDragPreviewChange={onPartitionDragPreviewChange}
					interiorEdit={interiorEdit}
					onInteriorLayoutChange={onInteriorLayoutChange}
					onInteriorDragPreviewChange={handleInteriorDragPreviewChange}
				/>
			)}
			<BlueprintOverride active={viewMode === "blueprint"} />
			<AssemblyFilmOverride
				frame={assemblyFilm}
				modelRevision={modelRevision}
				groundY={accentGroundY}
				onSceneInfo={onAssemblyFilmSceneInfo}
				onScaleChange={onAssemblyFilmScaleChange}
			/>
			{interactive && isDraggingWindow && (
				<DragController
					buildInput={buildInput}
					rigRef={saunaRigRef}
					isDraggingWindow={isDraggingWindow}
					onWindowPlaced={onWindowPlaced}
					onOpeningMoved={onOpeningMoved}
					onPartitionSplitChange={onPartitionSplitChange}
					onDragCancelled={onDragCancelled}
					controlsRef={controlsRef}
				/>
			)}
		</Canvas>
	);
}
